import React from "react";
import { Form } from "react-bootstrap";
import { Skeleton } from "@mui/material";
import "./Select.scss";

/**
 * Componente Select con validación y estados visuales
 * @param {Object} props - Props del componente
 * @param {string} props.name - Nombre del select para formularios
 * @param {string|number} [props.value] - Valor actual del select
 * @param {Function} [props.onChange] - Función ejecutada al cambiar el valor
 * @param {string} [props.label] - Etiqueta del select
 * @param {string} [props.errorMessage] - Mensaje de error a mostrar
 * @param {boolean} [props.disabled=false] - Estado deshabilitado del select
 * @param {boolean} [props.isValid] - Estado de validación exitosa
 * @param {boolean} [props.isInvalid] - Estado de validación fallida
 * @param {boolean} [props.isLoading=false] - Estado de carga que muestra skeleton
 * @param {string} [props.id] - ID único del select
 * @param {string} [props.className=''] - Clases CSS adicionales
 * @param {Object} [props.style={}] - Estilos inline adicionales
 * @param {React.ReactNode} props.children - Opciones del select
 * @param {Object} [...props] - Props adicionales del elemento select
 * @returns {JSX.Element} Componente Select
 */
export const Select = ({
  name,
  value,
  onChange,
  label,
  errorMessage,
  disabled = false,
  isValid,
  isInvalid,
  isLoading = false,
  id,
  className = "",
  style = {},
  children,
  ...props
}) => {
  const shouldShowValid = isValid && !disabled;
  const shouldShowInvalid = isInvalid && !disabled;

  const selectClasses = ["select-custom", className].filter(Boolean).join(" ");

  const labelClasses = [
    "label-custom",
    shouldShowInvalid && "error-label",
    shouldShowValid && "success-label",
  ]
    .filter(Boolean)
    .join(" ");

  if (isLoading) {
    return (
      <Form.Group style={style}>
        {label && (
          <Skeleton
            variant="text"
            width="40%"
            height={16.8}
            sx={{ marginBottom: "8px", marginLeft: "16px" }}
          />
        )}
        <Skeleton
          variant="rounded"
          width="100%"
          height={48}
          sx={{ borderRadius: "8px" }}
        />
      </Form.Group>
    );
  }

  return (
    <Form.Group style={style}>
      {label && (
        <Form.Label htmlFor={id} className={labelClasses}>
          {label}
        </Form.Label>
      )}

      <div className="select-wrapper">
        <Form.Select
          id={id}
          name={name}
          value={value}
          onChange={onChange}
          disabled={disabled}
          isValid={shouldShowValid}
          isInvalid={shouldShowInvalid}
          className={selectClasses}
          {...props}
        >
          {children}
        </Form.Select>
      </div>

      {shouldShowInvalid && errorMessage && (
        <div className="error-select">{errorMessage}</div>
      )}
    </Form.Group>
  );
};
